import { useCallback, useEffect, useRef, useState } from 'react';
import '../styles/auraosShowcase.css';
import '../styles/homeAuraos.css';
import { Avatar, CHAT, ChevronRight, HelpIcon, Keycap, SellByChatView, StaffByline } from './auraos/PortalKit';
import { HomeCalendar, type CalItem } from './HomeCalendar';
import { CarouselPreview, RunCard, THUMBS, VideoTile } from './auraos/StudioParts';
import { CtaLink, CTA_LABEL } from './auraos/walkthrough';
import { staffBySlug } from '../data/aiStaff';
import { trackEvent } from '../lib/analytics';

/**
 * The home hero dashboard: a sample of the real AuraOS client portal, built
 * from the same pieces as the /auraos replicas (browser frame, top bar, one
 * page of sections, no sidebar). Sections can be dragged to reorder and
 * expanded or shrunk, as in the real portal. It autoplays a short loop and every part is
 * clickable (setup chips, staff, Like it / Approve / Needs work, the account
 * menu). A real client's portal shows the AI Staff they turn on, so there are
 * three sample setups. Sample content only; nothing is sent anywhere.
 */

type View = 'home' | 'team' | 'library' | 'settings';
type SecId = 'calendar' | 'chat' | 'news' | 'library' | 'messages';
type Size = 'full' | 'half';
type ItemState = 'idle' | 'like' | 'approved' | 'needs' | 'sent';

type Media = { type: 'video'; src: string; dur: string; note?: string } | { type: 'carousel' };
interface QueueItem { slug: string; kind: string; title: string; text: string; media?: Media }
interface Setup {
  id: string;
  label: string;
  area: { key: string; label: string; tile: string; heading: string };
  rail: string[];
  staff: { slug: string; line: string }[];
  queue: QueueItem[];
  message: string;
  library: string[];
}

const SETUPS: Setup[] = [
  {
    id: 'marketing',
    label: 'Marketing team',
    area: { key: 'area', label: 'Content calendar', tile: '6 items planned', heading: 'Your content calendar' },
    rail: ['Messages & approvals', 'Content calendar', 'Library', 'Team', 'Settings'],
    staff: [
      { slug: 'marketing-content', line: 'Blog, social, newsletters and campaign assets in your brand voice, on a calendar.' },
      { slug: 'social-media', line: 'Runs the calendar across platforms and schedules the posts you approve.' },
      { slug: 'email-newsletter', line: 'Broadcasts, nurture sequences and the newsletter, on your own list.' },
      { slug: 'seo-strategist', line: 'Keyword research, content briefs and on-page fixes, ranked into a roadmap.' },
      { slug: 'video-short-form', line: 'Turns recordings into edited short videos with captions, ready for review.' },
    ],
    queue: [
      { slug: 'marketing-content', kind: 'Blog post', title: '5 questions to ask before you hire a contractor', text: 'A plain checklist your customers can use this week, with a short call to book.' },
      { slug: 'video-short-form', kind: 'Short-form video', title: 'Behind the counter: a 40-second tour', text: 'Cut to the strongest line first, captions on, ready for Reels and Shorts.' },
      { slug: 'marketing-content', kind: 'LinkedIn post', title: 'What we learned from 100 service calls', text: 'Three patterns from last quarter, written in your voice.' },
    ],
    message: 'Your May newsletter is drafted and waiting in approvals.',
    library: ['Spring promo email · Approved', 'Holiday hours post · Approved'],
  },
  {
    id: 'sales',
    label: 'Sales team',
    area: { key: 'area', label: 'Sell by Chat', tile: '3 live conversations', heading: 'Sell by Chat · Your live sales conversations' },
    rail: ['Messages & approvals', 'Sell by Chat', 'Library', 'Team', 'Settings'],
    staff: [
      { slug: 'lead-manager', line: 'Captures and scores new leads, drafts first-touch replies and books meetings.' },
      { slug: 'sell-by-chat', line: 'Greets, qualifies and answers in chat, then closes to a payment link, a booked call or a hand-off to you.' },
      { slug: 'sales-closer', line: 'Works the pipeline with follow-ups and re-engages leads that went quiet.' },
      { slug: 'proposal-quote', line: 'Turns a deal’s details into a tailored proposal or quote in minutes.' },
      { slug: 'scheduling-appointments', line: 'Books, reschedules and reminds, and recovers no-shows.' },
    ],
    queue: [
      { slug: 'lead-manager', kind: 'First-touch outreach', title: 'Reply to Jordan, kitchen remodel inquiry', text: 'Thanks for reaching out about the remodel. Here are two times this week for a quick call.' },
      { slug: 'scheduling-appointments', kind: 'No-show follow-up', title: 'Missed consult, Tuesday 2:00', text: 'Sorry we missed you. Here is a link to pick a new time that works.' },
    ],
    message: 'Two new leads came in overnight. Rohan drafted both replies.',
    library: ['Spring pricing sheet · Approved'],
  },
  {
    id: 'content',
    label: 'Content team',
    area: { key: 'area', label: 'Content calendar', tile: '5 items planned', heading: 'Your content calendar' },
    rail: ['Messages & approvals', 'Content calendar', 'Library', 'Team', 'Settings'],
    staff: [
      { slug: 'video-short-form', line: 'Edits your recordings into Shorts and long-form videos.' },
      { slug: 'marketing-content', line: 'Writes your posts, LinkedIn carousels and articles in your voice.' },
      { slug: 'social-media', line: 'Keeps the content calendar full and on time.' },
      { slug: 'content-repurposer', line: 'Turns one video or article into a week of posts.' },
      { slug: 'creative-designer', line: 'Designs images and thumbnails in your look.' },
    ],
    queue: [
      { slug: 'video-short-form', kind: 'Short-form video', title: '3 mistakes new homeowners make', text: 'Cut to the strongest line first, captions on, ready for Reels, Shorts and TikTok.', media: { type: 'video', src: THUMBS.mistakes, dur: '0:38' } },
      { slug: 'marketing-content', kind: 'LinkedIn carousel', title: '5 ways we cut our wait time', text: 'Named tips, five slides, ready as a LinkedIn PDF.', media: { type: 'carousel' } },
      { slug: 'video-short-form', kind: 'Short-form video', title: 'From our 20-minute talk: the one tip that saves a weekend', text: 'Cut from a long video, edited on its own.', media: { type: 'video', src: THUMBS.weekend, dur: '0:41', note: '1 of 4 Shorts from this recording' } },
    ],
    message: 'Your recording is edited. Three Shorts are waiting in approvals.',
    library: ['Kitchen tips: Short · Approved', 'Holiday hours post · Approved'],
  },
  {
    id: 'back',
    label: 'Back office',
    area: { key: 'library', label: 'Library', tile: '12 approved this month', heading: 'Library' },
    rail: ['Messages & approvals', 'Library', 'Team', 'Settings'],
    staff: [
      { slug: 'invoicing-collections', line: 'Issues invoices, tracks payment and sends polite follow-ups.' },
      { slug: 'bookkeeper-payroll', line: 'Prepares the monthly books, flags anything unusual and reminds you of tax dates. Never moves money.' },
      { slug: 'contracts-legal', line: 'Drafts contracts and NDAs from your terms. Every draft needs licensed review before it is sent.' },
      { slug: 'customer-support', line: 'Answers routine questions from your knowledge base and hands you the rest.' },
    ],
    queue: [
      { slug: 'bookkeeper-payroll', kind: 'Monthly books', title: 'September books', text: 'Income and expenses by category, with two items flagged for a look.' },
      { slug: 'contracts-legal', kind: 'Contract draft', title: 'Service agreement, Lakeside Dental', text: 'Drafted from the agreed terms. Needs licensed review before it is sent.' },
      { slug: 'bookkeeper-payroll', kind: 'Estimated taxes', title: 'Q4 estimated tax reminder', text: 'Your set-aside and the due date, so nothing sneaks up.' },
    ],
    message: 'Your August books are in the Library.',
    library: ['Invoice follow-up, Oak Street · Approved', 'August books · Approved'],
  },
];

const SECTIONS: Record<string, SecId[]> = {
  marketing: ['calendar', 'messages'],
  content: ['calendar', 'messages'],
  sales: ['chat', 'messages'],
  back: ['library', 'news', 'messages'],
};
const LABEL: Record<SecId, string> = { calendar: 'Content calendar', chat: 'Sell by Chat', news: 'What’s new', library: 'Library', messages: 'Messages & approvals' };

const CAL_CONTENT: CalItem[] = [
  { date: '2026-10-20', title: '3 mistakes new homeowners make', meta: 'Video & Short-Form \u00b7 Short-form video', status: 'Queued' },
  { date: '2026-10-21', title: '5 ways we cut our wait time', meta: 'Marketing & Content \u00b7 LinkedIn carousel', status: 'Queued' },
  { date: '2026-10-23', title: 'The one tip that saves a weekend', meta: 'Video & Short-Form \u00b7 Short-form video', status: 'Scheduled' },
  { date: '2026-10-27', title: 'Why quotes take two days, and how we fixed it', meta: 'Video & Short-Form \u00b7 Short-form video', status: 'Scheduled' },
  { date: '2026-10-30', title: 'One text, not three calls', meta: 'Marketing & Content \u00b7 LinkedIn carousel', status: 'Scheduled' },
];

const CAL_MARKETING: CalItem[] = [
  { date: '2026-10-19', title: 'Newsletter: October roundup', meta: 'Email & Newsletter · Newsletter', status: 'Queued' },
  { date: '2026-10-20', title: '5 questions to ask before you hire a contractor', meta: 'Marketing & Content · Blog post', status: 'Queued' },
  { date: '2026-10-22', title: 'What we learned from 100 service calls', meta: 'Marketing & Content · LinkedIn post', status: 'Scheduled' },
  { date: '2026-10-23', title: 'Behind the counter: a 40-second tour', meta: 'Video & Short-Form · Short-form video', status: 'Queued' },
  { date: '2026-10-26', title: 'Weekend recap', meta: 'Social Media Manager · Instagram post', status: 'Scheduled' },
  { date: '2026-10-29', title: 'Halloween latte art', meta: 'Social Media Manager · Instagram post', status: 'Scheduled' },
];

const CAL_BY: Record<string, CalItem[]> = { marketing: CAL_MARKETING, content: CAL_CONTENT, sales: [], back: [] };

/** One loop: Marketing (0-3), Sales (4), Content (5-8), Back office (9), then restart. About 35s. */
const BEATS = [2600, 2400, 2200, 2200, 8500, 3500, 3000, 3000, 2400, 5200];
const IDLE_RESUME_MS = 8000;
/** The beat where each setup's script starts: Marketing, Sales, Content, Back office. */
const FIRST_BEAT = [0, 4, 5, 9];
const TIMES = ['2h ago', 'just now', '1h ago'];

const prefersReduced = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const Grip = () => (
  <svg width="12" height="14" viewBox="0 0 12 16" fill="currentColor" aria-hidden="true">
    {[2, 8, 14].map((y) => [3, 9].map((x) => <circle key={`${x}${y}`} cx={x} cy={y} r="1.3" />))}
  </svg>
);
const PanelIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3.5" y="4.5" width="17" height="15" rx="3" /><path d="M9.5 4.5v15" /></svg>
);
const CheckIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>);
const XIcon = () => (<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>);
const CalGlyph = () => (
  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><rect x="3.5" y="5" width="17" height="15" rx="3" /><path d="M3.5 10h17M8 3v4M16 3v4" /></svg>
);

type Lay = { order: SecId[]; sizes: Record<string, Size> };

export const HomeDashboard = () => {
  const [setupI, setSetupI] = useState(0);
  const [view, setView] = useState<View>('home');
  const [menuOpen, setMenuOpen] = useState(false);
  const [calOpen, setCalOpen] = useState(false);
  const [states, setStates] = useState<Record<string, ItemState>>({});
  const [layout, setLayout] = useState<Record<string, Lay>>({});
  const [dragId, setDragId] = useState<SecId | null>(null);
  const [beat, setBeat] = useState(0);
  const [runKey, setRunKey] = useState(0);
  const setupRef = useRef(0);
  const [hot, setHot] = useState(false);
  const [visible, setVisible] = useState(false);
  const [paused, setPaused] = useState(false);
  const [just, setJust] = useState<{ sid: string; i: number } | null>(null);
  const [runStage, setRunStage] = useState(3);
  const [slide, setSlide] = useState(0);
  const [chat, setChat] = useState<{ shown: number; typing: boolean; won: boolean }>({ shown: CHAT.length, typing: false, won: true });
  const hostRef = useRef<HTMLDivElement>(null);
  const reduced = useRef(false);
  const resumeTimer = useRef<number | undefined>(undefined);
  const decideTimer = useRef<number | undefined>(undefined);

  const setup = SETUPS[setupI];
  setupRef.current = setupI;
  const lay: Lay = layout[setup.id] ?? { order: SECTIONS[setup.id], sizes: {} };
  const sizeOf = (id: SecId): Size => lay.sizes[id] ?? 'full';
  const stateOf = (i: number): ItemState => states[`${setup.id}:${i}`] ?? 'idle';
  const decided = (i: number) => stateOf(i) === 'approved' || stateOf(i) === 'sent';
  const waiting = setup.queue.filter((_, i) => !decided(i)).length;
  const justI = just && just.sid === setup.id && decided(just.i) ? just.i : -1;
  const firstOpen = setup.queue.findIndex((_, i) => !decided(i));
  const currentI = justI >= 0 ? justI : firstOpen;
  const showDone = waiting === 0 && justI < 0;

  const calItems: CalItem[] = (CAL_BY[setup.id] ?? CAL_CONTENT).map((c) =>
    c.date === '2026-10-20' && states[`${setup.id}:0`] === 'approved' ? { ...c, status: 'Scheduled' as const } : c);

  const setItem = useCallback((sid: string, i: number, st: ItemState) => {
    setStates((m) => ({ ...m, [`${sid}:${i}`]: st }));
  }, []);

  const showResult = (sid: string, i: number) => {
    setJust({ sid, i });
    window.clearTimeout(decideTimer.current);
    decideTimer.current = window.setTimeout(() => setJust(null), 1800);
  };

  useEffect(() => {
    reduced.current = prefersReduced();
    const el = hostRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setVisible(e.isIntersecting), { threshold: 0.1 });
    io.observe(el);
    return () => { io.disconnect(); window.clearTimeout(resumeTimer.current); window.clearTimeout(decideTimer.current); };
  }, []);

  // Sales setup: the conversation plays out message by message, with typing dots,
  // like the /auraos "Sell by Chat" view. Reduced motion shows it complete.
  useEffect(() => {
    if (setup.id !== 'sales') return;
    if (reduced.current) { setChat({ shown: CHAT.length, typing: false, won: true }); return; }
    if (!visible) return;
    const timers: number[] = [];
    const at = (ms: number, st: { shown: number; typing: boolean; won: boolean }) => timers.push(window.setTimeout(() => setChat(st), ms));
    setChat({ shown: 0, typing: false, won: false });
    at(350, { shown: 1, typing: false, won: false });
    at(1150, { shown: 1, typing: true, won: false });
    at(2200, { shown: 2, typing: false, won: false });
    at(3250, { shown: 3, typing: false, won: false });
    at(3950, { shown: 3, typing: true, won: false });
    at(5000, { shown: 4, typing: false, won: false });
    at(6000, { shown: 5, typing: false, won: false });
    at(6700, { shown: 5, typing: true, won: false });
    at(7550, { shown: 6, typing: false, won: true });
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [setup.id, visible, runKey]);

  // Autoplay: apply the beat's action, then wait its duration.
  useEffect(() => {
    if (reduced.current || paused || !visible) return;
    setHot(false);
    const timers: number[] = [];
    const later = (fn: () => void, ms: number) => { timers.push(window.setTimeout(fn, ms)); };
    switch (beat) {
      case 0: setSetupI(0); setView('home'); setMenuOpen(false); setCalOpen(false); setStates({}); setLayout({}); setRunStage(3); setSlide(0); break;
      case 1: setItem('marketing', 0, 'like'); break;
      case 2: setItem('marketing', 0, 'approved'); showResult('marketing', 0); break;
      case 3: setHot(true); break;
      case 4: setSetupI(1); setHot(true); break;
      case 5: setSetupI(2); clearSetup('content'); setRunStage(0); later(() => setRunStage(1), 800); later(() => setRunStage(2), 1600); later(() => setRunStage(3), 2400); break;
      case 6: setItem('content', 0, 'like'); later(() => { setItem('content', 0, 'approved'); showResult('content', 0); }, 1300); break;
      case 7: setSlide(0); later(() => setSlide(1), 800); later(() => setSlide(2), 1600); break;
      case 8: setHot(true); break;
      case 9: setSetupI(3); clearSetup('back'); setHot(true); later(() => setItem('back', 0, 'like'), 1200); later(() => { setItem('back', 0, 'approved'); showResult('back', 0); }, 2600); break;
      default: break;
    }
    timers.push(window.setTimeout(() => setBeat((b) => (b + 1) % BEATS.length), BEATS[beat]));
    return () => timers.forEach((t) => window.clearTimeout(t));
  }, [beat, runKey, paused, visible, setItem]);

  const clearSetup = (id: string) => setStates((m) => {
    const n = { ...m };
    Object.keys(n).forEach((k) => { if (k.startsWith(`${id}:`)) delete n[k]; });
    return n;
  });

  const touch = () => {
    if (reduced.current) return;
    setPaused(true);
    window.clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => {
      // Carry on from the setup the visitor is looking at, then on through the rest of the loop.
      clearSetup(SETUPS[setupRef.current].id); setLayout({}); setHot(false); setCalOpen(false); setRunStage(3); setSlide(0);
      setBeat(FIRST_BEAT[setupRef.current]); setRunKey((k) => k + 1); setPaused(false);
    }, IDLE_RESUME_MS);
  };

  const pickSetup = (i: number) => {
    setView('home'); setMenuOpen(false); setCalOpen(false); setLayout({});
    if (reduced.current) { setSetupI(i); }
    else {
      // Choosing a pill plays that setup's script right away, then the loop carries on in order.
      window.clearTimeout(resumeTimer.current);
      clearSetup(SETUPS[i].id); setRunStage(3); setSlide(0); setHot(false);
      setSetupI(i); setBeat(FIRST_BEAT[i]); setRunKey((k) => k + 1); setPaused(false);
    }
    trackEvent('home_dashboard_setup', { setup: SETUPS[i].id });
  };
  const openView = (v: View) => { touch(); setView(v); setMenuOpen(false); trackEvent('home_dashboard_menu', { view: v }); };
  const decide = (i: number, st: ItemState) => {
    touch(); setItem(setup.id, i, st);
    if (st === 'approved' || st === 'sent') {
      trackEvent('home_dashboard_action', { action: st, setup: setup.id });
      showResult(setup.id, i);
    }
  };

  const updateLayout = (fn: (l: Lay) => Lay) => {
    touch();
    setLayout((m) => ({ ...m, [setup.id]: fn(m[setup.id] ?? { order: SECTIONS[setup.id], sizes: {} }) }));
  };
  const move = (id: SecId, dir: -1 | 1) => updateLayout((l) => {
    const i = l.order.indexOf(id); const j = i + dir;
    if (j < 0 || j >= l.order.length) return l;
    const order = [...l.order]; [order[i], order[j]] = [order[j], order[i]];
    trackEvent('home_dashboard_layout', { action: 'move', section: id });
    return { ...l, order };
  });
  const toggleSize = (id: SecId) => updateLayout((l) => {
    trackEvent('home_dashboard_layout', { action: 'resize', section: id });
    return { ...l, sizes: { ...l.sizes, [id]: (l.sizes[id] ?? 'full') === 'full' ? 'half' : 'full' } };
  });
  const dropOn = (target: SecId) => {
    if (!dragId || dragId === target) return;
    const from = dragId;
    updateLayout((l) => {
      const order = l.order.filter((x) => x !== from);
      order.splice(l.order.indexOf(target), 0, from);
      trackEvent('home_dashboard_layout', { action: 'drag', section: from });
      return { ...l, order };
    });
    setDragId(null);
  };

  const cur = currentI >= 0 ? setup.queue[currentI] : null;
  const curState = currentI >= 0 ? stateOf(currentI) : 'idle';
  const nextCal = calItems[0];

  const renderItem = () => {
    if (!cur) return null;
    const st = curState;
    return (
      <div className="a-card a-item hd-item" key={`${setup.id}-${currentI}-${decided(currentI)}`}>
        <StaffByline slug={cur.slug} size={28} />
        <div className="a-item-meta">
          <span className="a-pill a-pill-kind">{cur.kind}</span>
          <time>{TIMES[currentI % TIMES.length]}</time>
        </div>
        <h4>{cur.title}</h4>
        <p className="ex">{cur.text}</p>
        {cur.media?.type === 'video' && <VideoTile src={cur.media.src} dur={cur.media.dur} note={cur.media.note} />}
        {cur.media?.type === 'carousel' && <CarouselPreview slide={slide} onSlide={(n) => { touch(); setSlide(n); }} />}
        <span className="hd-preview"><ChevronRight /> Preview the full piece</span>
        {st === 'approved' && (
          <div className="a-approved"><b>Approved. It&rsquo;s yours to use.</b><span>Saved for your records. Thanks for the notes: the agent will keep doing this.</span></div>
        )}
        {st === 'sent' && (
          <div className="a-approved hd-sent"><b>Thanks, we&rsquo;ll adjust.</b><span>Your notes went straight to the team and the agent&rsquo;s learning. A revised version will come back to you.</span></div>
        )}
        {!decided(currentI) && (
          <>
            <p className="a-ask">How&rsquo;s this draft?</p>
            <div className="a-vote">
              <button type="button" className={`like${st === 'like' ? ' hot' : ''}`} onClick={() => decide(currentI, 'like')}><CheckIcon /> Like it</button>
              <button type="button" className={`bad${st === 'needs' ? ' hot' : ''}`} onClick={() => decide(currentI, 'needs')}><XIcon /> Needs work</button>
            </div>
          </>
        )}
        {st === 'like' && (
          <div className="a-feedback">
            <label>Great. What did you like about it? (optional, helps the agent keep doing it)</label>
            <div className="a-textarea">The tone and the opening line.</div>
            <div className="a-feedback-actions">
              <button type="button" className="a-btn-primary" onClick={() => decide(currentI, 'approved')}><CheckIcon /> Approve</button>
              <button type="button" className="a-btn-ghost" onClick={() => decide(currentI, 'idle')}>Back</button>
            </div>
          </div>
        )}
        {st === 'needs' && (
          <div className="a-feedback">
            <label>What didn&rsquo;t work, or why does it need work? (required, so the agent learns)</label>
            <div className="a-textarea">Too long for a text.</div>
            <label style={{ marginTop: 10 }}>What would you like better? (optional, trains it faster)</label>
            <div className="a-textarea">Two sentences, offer first.</div>
            <div className="a-feedback-actions">
              <button type="button" className="a-btn-primary" onClick={() => decide(currentI, 'sent')}>Send feedback</button>
              <button type="button" className="a-btn-ghost" onClick={() => decide(currentI, 'idle')}>Back</button>
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderApprovals = () => (
    <div>
      <h3 className="a-h">Your approvals <span>&middot; {waiting} waiting</span></h3>
      <div className="a-card a-approvals hd-approvals">
        {showDone ? (
          <div className="hd-done">
            <b>Nothing waiting.</b>
            <span>Nothing goes out until you say so.</span>
            <button type="button" className="a-btn-ghost" onClick={() => { touch(); setStates((m) => { const n = { ...m }; setup.queue.forEach((_, i) => delete n[`${setup.id}:${i}`]); return n; }); }}>Start over</button>
          </div>
        ) : (
          <>
            {renderItem()}
            {setup.queue.map((q, i) => (!decided(i) && i !== currentI ? <p key={i} className="hd-next">Next: {q.title}</p> : null))}
          </>
        )}
      </div>
    </div>
  );

  const renderMessages = () => (
    <div>
      <h3 className="a-h">Message BRANDCENTRAL</h3>
      <div className="a-card hd-chat">
        <div className="hd-chat-h">
          <span className="hd-chat-logo"><Keycap /></span>
          <span><b>BRANDCENTRAL</b><i>Your account team</i></span>
        </div>
        <div className="hd-chat-body">
          <div className="hd-chat-row">
            <span className="hd-chat-av"><Keycap /></span>
            <div><span className="hd-bubble">{setup.message}</span><small>BRANDCENTRAL &middot; 2h ago</small></div>
          </div>
        </div>
        <div className="hd-composer">
          <span className="hd-clip" aria-hidden="true"><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5l-8.6 8.6a5 5 0 0 1-7-7l8.6-8.6a3.4 3.4 0 0 1 4.8 4.8l-8.6 8.6a1.7 1.7 0 0 1-2.4-2.4l8-8" /></svg></span>
          <span className="hd-input">Type a message&hellip;</span>
          <span className="hd-send">Send</span>
        </div>
      </div>
    </div>
  );

  const renderBody = (id: SecId) => {
    if (id === 'calendar') {
      const nextKind = nextCal.meta.split(' \u00b7 ')[1] ?? nextCal.title;
      const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][new Date(`${nextCal.date}T12:00:00`).getDay()];
      return (
        <div className="hd-calwrap">
          <div className={`hd-calrow a-card${hot ? ' hot' : ''}`}>
            <span className="hd-cal-tile"><CalGlyph /></span>
            <span className="hd-cal-t"><b>Your content calendar</b><span>{calItems.length} items planned &middot; next: {wd} {nextKind}</span></span>
            <button type="button" className="hd-open" onClick={() => { touch(); setCalOpen(true); trackEvent('home_dashboard_calendar'); }}>Open</button>
          </div>
          {setup.id === 'content' && <RunCard stage={runStage} />}
        </div>
      );
    }
    if (id === 'chat') {
      return (
        <div className={`hd-area${hot ? ' hot' : ''}`}>
          <SellByChatView shown={chat.shown} typing={chat.typing} taylorStatus={chat.won ? 'won' : 'ai'} wonFlash={chat.won && !reduced.current} taylorPulse={chat.typing} showYou={chat.shown >= CHAT.length} riseFrom={chat.shown - 1} />
        </div>
      );
    }
    if (id === 'library') {
      const fresh = setup.queue.map((q, i) => ({ q, i })).filter(({ i }) => states[`${setup.id}:${i}`] === 'approved').reverse();
      return (
        <div className={`hd-area${hot ? ' hot' : ''}`}>
          <div className="a-lib">
            <p>Everything your AI Staff produced that&rsquo;s been approved, ready to read, copy or download.</p>
            <p className="a-caps">This month</p>
            {fresh.map(({ q }) => (
              <div key={q.title} className="a-card a-lib-row new"><span><b>{q.title}</b><span>{q.kind} &middot; {staffBySlug(q.slug).name} &middot; approved today</span></span><em>Open</em></div>
            ))}
            <div className="a-card a-lib-row"><span><b>Invoice follow-up, Oak Street</b><span>Invoice &middot; Budi &middot; approved Sep 28</span></span><em>Open</em></div>
            <p className="a-caps">August</p>
            <div className="a-card a-lib-row"><span><b>August books</b><span>Monthly books &middot; Meera &middot; approved Sep 3</span></span><em>Open</em></div>
          </div>
        </div>
      );
    }
    if (id === 'news') {
      return (
        <div className={`a-card hd-news${hot ? ' hot' : ''}`}>
          <b>Arrange your portal your way</b>
          <p>Drag a section by its handle to move it, and use Shrink or Expand to change its size. Your portal shows the AI Staff you turn on.</p>
        </div>
      );
    }
    return <div className="hd-cols">{renderMessages()}{renderApprovals()}</div>;
  };

  return (
    <div className="hd-wrap" ref={hostRef} onPointerDown={touch}>
      <div className="hd-chips" role="tablist" aria-label="Sample portal setups">
        {SETUPS.map((s, i) => (
          <button key={s.id} type="button" role="tab" aria-selected={i === setupI} className={`hd-setup${i === setupI ? ' on' : ''}`} onClick={() => pickSetup(i)}>{s.label}</button>
        ))}
      </div>

      <div className="aura-app hd-portal">
        <div className="aos-frame hd-frame">
          <div className="aos-frame-bar">
            <span className="aos-frame-dots"><i /><i /><i /></span>
            <span className="aos-frame-url">app.brandctl.com/portal</span>
            <span style={{ width: 42 }} />
          </div>
          <div className="a-top">
            <div className="a-brand">
              <Keycap />
              <span className="a-wordmark"><b>BRANDCENTRAL</b><span>AuraOS</span></span>
            </div>
            <div className="a-top-right">
              <span className="a-btn-ghost a-help"><HelpIcon />Help</span>
              <button type="button" className="a-profile pill" aria-haspopup="menu" aria-expanded={menuOpen} onClick={() => { touch(); setMenuOpen((o) => !o); }}>
                <span className="a-initial">J</span>Jordan<ChevronRight />
              </button>
            </div>
            {menuOpen && (
              <div className="a-menu" role="menu">
                {(['team', 'library', 'settings'] as View[]).map((v) => (
                  <button key={v} type="button" role="menuitem" className="hd-menu-i" onClick={() => openView(v)}>{v[0].toUpperCase() + v.slice(1)}</button>
                ))}
              </div>
            )}
          </div>

          <div className="a-body hd-pbody">
            {view !== 'home' && (
              <button type="button" className="hd-back" onClick={() => openView('home')}>&larr; Back to your portal</button>
            )}

            {view === 'home' && (
              <div className="hd-grid">
                {lay.order.map((id, idx) => (
                  <section
                    key={id}
                    className={`hd-sec ${sizeOf(id)}${dragId === id ? ' dragging' : ''}`}
                    onDragOver={(e) => { if (dragId) e.preventDefault(); }}
                    onDrop={() => dropOn(id)}
                  >
                    <div className="hd-sec-h">
                      <span className="hd-grip" draggable aria-label={`Drag to move ${LABEL[id]}`} onDragStart={() => { touch(); setDragId(id); }} onDragEnd={() => setDragId(null)}><Grip /></span>
                      <span className="hd-sec-t">{LABEL[id]}</span>
                      <span className="hd-sec-tools">
                        <button type="button" className="hd-mv" aria-label={`Move ${LABEL[id]} up`} disabled={idx === 0} onClick={() => move(id, -1)}>&uarr;</button>
                        <button type="button" className="hd-mv" aria-label={`Move ${LABEL[id]} down`} disabled={idx === lay.order.length - 1} onClick={() => move(id, 1)}>&darr;</button>
                        <button type="button" className="hd-shrink" onClick={() => toggleSize(id)}><PanelIcon />{sizeOf(id) === 'full' ? 'Shrink' : 'Expand'}</button>
                      </span>
                    </div>
                    {renderBody(id)}
                  </section>
                ))}
              </div>
            )}

            {view === 'team' && (
              <div className="a-lib">
                <h3>Team</h3>
                <p>The AI Staff turned on for this sample setup. Turn AI Staff on or pause them anytime.</p>
                {setup.staff.map((s) => {
                  const m = staffBySlug(s.slug);
                  return (
                    <div key={s.slug} className="a-card hd-team-row">
                      <Avatar slug={s.slug} size={44} />
                      <span className="hd-team-t"><b>{m.name} <i>{m.role}</i></b><span>{s.line}</span>
                        <CtaLink source="home-dashboard" notes={`From the home page: ${m.name} (${m.role})`} className="hd-link">{CTA_LABEL}</CtaLink>
                      </span>
                      <em className="hd-live">Live</em>
                    </div>
                  );
                })}
              </div>
            )}
            {view === 'library' && (
              <div className="a-lib">
                <h3>Library</h3>
                <p>Everything your AI Staff produced that&rsquo;s been approved, ready to read, copy or download.</p>
                <p className="a-caps">Recently approved</p>
                {setup.library.map((l) => l.replace(' · Approved', '')).map((t) => (
                  <div key={t} className="a-card a-lib-row"><span><b>{t}</b><span>Approved</span></span><em>Open</em></div>
                ))}
              </div>
            )}
            {view === 'settings' && (
              <div className="a-lib">
                <h3>Settings</h3>
                <p>Turn AI Staff on or pause them anytime. Your portal shows the AI Staff you turn on, and you can drag its sections to reorder them.</p>
              </div>
            )}
            <p className="hd-foot">Powered by BRANDCENTRAL AI &middot; your work stays yours</p>
          </div>

          {calOpen && <HomeCalendar items={calItems} onClose={() => setCalOpen(false)} onInteract={touch} />}
        </div>
      </div>
      {setup.id === 'content' && (
        <p className="hd-cap hd-cap-studio">Made in the Content Studio: your AI Staff edit the videos and write the posts, and nothing goes out until you approve it. Drop recordings in a shared folder and new ones become Shorts on their own.</p>
      )}
      <p className="hd-cap">Your portal shows the AI Staff you turn on. Sections can be moved, expanded and shrunk. This is one sample setup.</p>
    </div>
  );
};
